import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
    buildEntryAndExitForm,
    MATERIAL_CONTROL_TABS,
    VEHICLE_MOVEMENT_FORM_MODEL
} from "../../Model/Model";
import HandleChange from "../../../../../Helpers/HandleChange";
import {PHOTO_PICKER_MODEL} from "../../../../../components/PhotoPicker/SubComponent/Models/Models";
import routes from "../../../../../Routes/routes";
import {CreateMaterialControl} from "../../../../../reduxToolkit/MaterialControl/MaterialControlApi";
import {errorMsg} from "../../../../../Apis/functions";
import {USER_TYPE_NAME_MODEL} from "../../../../../enums/userTypeEnum";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Creation of a movement of a vehicle.
 *
 * Copied from the creation of an entry and exit form, so the movement is
 * captured with the same fields while its own ones are defined. The movement is
 * not registered with a type of form, so it holds the model of the general
 * entry and exit.
 */
const CreateVehicleMovementApp = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission, user } = useSelector((state) => state.authenticatioauthennSlice);
    // A guard carries no permissions, its user type opens the folios it works on at the gate
    const isGuard = user?.data?.userType?.name === USER_TYPE_NAME_MODEL.GUARD;
    const [emptyFields, setEmptyFields] = useState(false);
    const [emptyFieldsItems, setEmptyFieldsItems] = useState(false);
    const [materialRequest, setMaterialRequest] = useState(buildEntryAndExitForm(VEHICLE_MOVEMENT_FORM_MODEL));
    const [photoPicker, setPhotoPicker] = useState({ ...PHOTO_PICKER_MODEL });
    const { handleChange } = HandleChange({ model: materialRequest, setModel: setMaterialRequest });
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [watchers, setWatchers] = useState([]);
    const [responsibleDepartments, setResponsibleDepartments] = useState([]);

    /**
     * Handles the movement creation process.
     *
     * Validates:
     * - General form fields.
     * - Movement items.
     * - Selected employees, watchers and responsible departments.
     * - Uploaded evidence files.
     *
     * If validation fails:
     * - Displays validation warnings.
     *
     * If validation succeeds:
     * - Sends the movement payload to the backend.
     * - Displays success feedback.
     * - Redirects the user back to the movements of the vehicles.
     *
     * @returns {void}
     */
    const handleAction = () => {
        if (!materialRequest.isValid({ photoPicker, selectedEmployees, watchers, responsibleDepartments }) || !materialRequest?.isValidItems()) {
            setEmptyFields(true);
            setEmptyFieldsItems(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }

        dispatch(CreateMaterialControl(materialRequest.toRegister({ selectedEmployees, watchers, responsibleDepartments, files: photoPicker.listFiles}))).then(({payload:{data:{success, code}}})=>{
            if (success){
                toast.success(t('has_been_created_successfully'));
                handleOnBack();
            }else errorMsg(code)
        });
    };

    /**
     * Navigates back to the movements of the vehicles, so the tab that opened
     * the creation is the one the user lands on.
     *
     * @returns {void}
     */
    const handleOnBack = () => {
        navigate(routes.materialControl.main, {
            state: {currentTab: MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX}
        });
    };

    /**
     * Resets the movement form to its initial state.
     *
     * Clears:
     * - General movement information.
     * - Uploaded files.
     * - Selected images.
     * - Item list data.
     * - Selected employees, watchers and responsible departments.
     *
     * @returns {void}
     */
    const resetForm = () => {
        setMaterialRequest(buildEntryAndExitForm(VEHICLE_MOVEMENT_FORM_MODEL));
        setPhotoPicker({ ...PHOTO_PICKER_MODEL });
        setSelectedEmployees([]);
        setWatchers([]);
        setResponsibleDepartments([]);
    };

    return {
        t,
        DEV,
        isGuard,
        permission,
        navigate,
        emptyFields,
        emptyFieldsItems,
        setEmptyFieldsItems,
        materialRequest,
        setMaterialRequest,
        handleChange,
        photoPicker,
        setPhotoPicker,
        selectedEmployees,
        setSelectedEmployees,
        watchers,
        setWatchers,
        responsibleDepartments,
        setResponsibleDepartments,
        resetForm,
        handleOnBack,
        handleAction
    };
};

export default CreateVehicleMovementApp;
